<template>
  <div
    :class="[
      'activity-cv-block',
      { 'activity-cv-block--visible': isAchievementsVisible },
    ]"
  >
    <el-divider></el-divider>

    <div class="activity__subtitle">
      Achievements
    </div>

    <div class="activity-cv-block__visibility-toggler">
      <el-button type="primary" @click="toggleAchievementsVisibility">
        {{ isAchievementsVisible ? 'Hide achievements' : 'Show achievements' }}
      </el-button>
    </div>

    <div class="activity-cv-block__content" v-if="isAchievementsVisible">
      <el-form
        class="activity-cv-block__item-form"
        label-position="top"
        v-for="(achievement, index) in achievementArray"
        :key="index"
        :rules="achievementFormRules"
        :model="achievement"
        ref="achievement"
      >
        <div class="row">
          <div class="col col-xs-12 col-md-6">
            <el-form-item label="Type" prop="achievementTypeId">
              <el-select
                class="wide"
                v-model="achievement.achievementTypeId"
                placeholder="Choose from the list"
              >
                <el-option
                  v-for="type in achievementTypeDescriptions"
                  :key="type.value"
                  :label="type.description"
                  :value="type.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item label="Difficulty" prop="achievementLevelTypeId">
              <el-select
                class="wide"
                v-model="achievement.achievementLevelTypeId"
                placeholder="Choose from the list"
              >
                <el-option
                  v-for="type in achievementLevelTypeDescriptions"
                  :key="type.value"
                  :label="type.description"
                  :value="type.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item label="Role" prop="achievementRoleTypeId">
              <el-select
                class="wide"
                v-model="achievement.achievementRoleTypeId"
                placeholder="Choose from the list"
              >
                <el-option
                  v-for="type in achievementRoleTypeDescriptions"
                  :key="type.value"
                  :label="type.description"
                  :value="type.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item label="Start day" prop="startDate">
              <el-date-picker
                class="wide"
                v-model="achievement.startDate"
                type="date"
                format="DD/MM/YYYY"
                @change="(date) => onStartDayChanged(date, achievement)"
              >
              </el-date-picker>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item label="End day" prop="endDate">
              <el-date-picker
                class="wide"
                v-model="achievement.endDate"
                type="date"
                format="DD/MM/YYYY"
              >
              </el-date-picker>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              label="Place"
              prop="place"
              :error="achievement.placeError"
            >
              <el-input v-model="achievement.place"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item
              label="Story"
              prop="story"
              :error="achievement.storyError"
            >
              <el-input
                type="textarea"
                :rows="3"
                v-model="achievement.story"
              ></el-input>
            </el-form-item>
          </div>
        </div>

        <div class="activity-cv-block__buttons">
          <confirmation-dialog
            content="Are you sure to delete this achievement?"
            @on-confirm="deleteAchievement(achievement)"
          >
            <el-button
              class="activity-cv-block__remove-button"
              type="primary"
              :disabled="isButtonDisabled('delete-achievement')"
            >
            <el-icon><Delete/></el-icon>
              Delete
            </el-button>
          </confirmation-dialog>
          <el-button
            type="primary"
            @click="validateAchievement(achievement, index)"
            :disabled="isButtonDisabled('save-achievement')"
          >
            Save achievement
          </el-button>
        </div>
      </el-form>

      <el-button type="primary" @click="addAchievement">
        <el-icon><Plus/></el-icon>
        Add achievement
      </el-button>
    </div>
  </div>
</template>

<script>
import {
  addAchievement,
  updateAchievement,
  removeAchievement,
  getAchievementTypeDescriptions,
  getAchievementLevelTypeDescriptions,
  getAchievementRoleTypeDescriptions,
} from '@/api/achievement.js';

import { showMessage } from '@/utils/messages.js';
import {
  parseErrorsByFields,
  isTwoObjectsEqual,
  removeArrayElementById,
  removeTimezoneOffset,
} from '@/utils/index.js';
import { disableButton } from '@/mixins/disableButton.js';

import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';

export default {
  name: 'EditAchievements',
  props: {
    achievementsData: {
      type: Array,
      required: false,
    },
    activityCVId: {
      type: Number,
      required: true,
    },
    activityId: {
      type: Number,
      required: true,
    },
  },
  mixins: [disableButton],
  components: {
    ConfirmationDialog,
  },
  data() {
    return {
      achievementArray: [],
      achievementTypeDescriptions: null,
      achievementLevelTypeDescriptions: null,
      achievementRoleTypeDescriptions: null,
      achievementFormRules: {
        achievementTypeId: [
          {
            required: true,
            message: 'Please choose achievement type',
            trigger: 'change',
          },
        ],
        achievementLevelTypeId: [
          {
            required: true,
            message: 'Please choose level of achievement',
            trigger: 'change',
          },
        ],
        achievementRoleTypeId: [
          {
            required: true,
            message: 'Please choose your role on this achievement',
            trigger: 'change',
          },
        ],
        story: [{ required: false, message: '', trigger: 'blur' }],
        place: [{ required: false, message: '', trigger: 'blur' }],
        startDate: [
          {
            required: true,
            message: 'Please choose date of start achievemtn',
            trigger: 'change',
          },
        ],
        endDate: [{ required: false, message: '', trigger: 'change' }],
      },
      isAchievementsVisible: false,
    };
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    isTwoObjectsEqual,
    removeArrayElementById,
    removeTimezoneOffset,
    async fetchAchievementTypeDescriptions() {
      const { activityId } = this;
      const { success, data } = await getAchievementTypeDescriptions({
        activityId,
      });
      if (success) {
        this.achievementTypeDescriptions = data.items;
      }
    },
    async fetchAchievementLevelTypeDescriptions() {
      const { activityId } = this;
      const { success, data } = await getAchievementLevelTypeDescriptions({
        activityId,
      });
      if (success) {
        this.achievementLevelTypeDescriptions = data.items;
      }
    },
    async fetchAchievementRoleTypeDescriptions() {
      const { activityId } = this;
      const { success, data } = await getAchievementRoleTypeDescriptions({
        activityId,
      });
      if (success) {
        this.achievementRoleTypeDescriptions = data.items;
      }
    },
    async requestAddAchievement(achievement) {
      this.disableButton('save-achievement');
      const { activityCVId, removeTimezoneOffset } = this;
      const {
        achievementTypeId,
        achievementLevelTypeId,
        achievementRoleTypeId,
        story,
        place,
        startDate,
        endDate,
      } = achievement;

      const { success, errors } = await addAchievement({
        activityCVId,
        achievementTypeId,
        achievementLevelTypeId,
        achievementRoleTypeId,
        story,
        place,
        startDate: startDate ? removeTimezoneOffset(startDate) : null,
        endDate: endDate ? removeTimezoneOffset(endDate) : null,
      });
      this.enableButton('save-achievement');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Ahievement has been added',
        });
        this.$emit('update-user-data');
      } else if (errors) {
        this.setAchievementErrors(
          achievement,
          this.parseErrorsByFields(errors),
        );
      }
    },
    async requestUpdateAchievement(achievement) {
      this.disableButton('save-achievement');
      const { removeTimezoneOffset } = this;
      const {
        achievementId,
        achievementTypeId,
        achievementLevelTypeId,
        achievementRoleTypeId,
        story,
        place,
        startDate,
        endDate,
      } = achievement;

      const { success, errors } = await updateAchievement({
        achievementId,
        achievementTypeId,
        achievementLevelTypeId,
        achievementRoleTypeId,
        story,
        place,
        startDate: startDate ? removeTimezoneOffset(startDate) : null,
        endDate: endDate ? removeTimezoneOffset(endDate) : null,
      });
      this.enableButton('save-achievement');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Ahievement has been updated',
        });
        this.$emit('update-user-data');
      } else if (errors) {
        this.setAchievementErrors(
          achievement,
          this.parseErrorsByFields(errors),
        );
      }
    },
    async requestRemoveAchievement(achievement) {
      this.disableButton('delete-achievement');
      const { achievementId } = achievement;
      const { success, errors } = await removeAchievement({ achievementId });
      this.enableButton('delete-achievement');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Ahievement has been removed',
        });
        this.$emit('update-user-data');
      } else if (errors) {
        this.setAchievementErrors(
          achievement,
          this.parseErrorsByFields(errors),
        );
      }
    },
    setAchievementErrors(achievement, errors) {
      const newErrors = {};
      for (const key in errors) {
        newErrors[key + 'Error'] = errors[key];
      }

      const achievementIndex = this.achievementArray.indexOf(achievement);
      this.achievementArray[achievementIndex] = {
        ...achievement,
        ...newErrors,
      };
    },
    setAchievementsData() {
      const { achievementsData } = this;

      this.achievementArray = achievementsData.map((achievement) => {
        return {
          achievementId: achievement.achievementId,
          achievementTypeId: achievement.achievementType
            ? achievement.achievementType.achievementTypeId
            : null,
          achievementLevelTypeId: achievement.achievementLevelType
            ? achievement.achievementLevelType.achievementLevelTypeId
            : null,
          achievementRoleTypeId: achievement.achievementRoleType
            ? achievement.achievementRoleType.achievementRoleTypeId
            : null,
          story: achievement.story,
          place: achievement.place,
          startDate: achievement.startDate,
          endDate: achievement.endDate,
        };
      });
    },
    addAchievement() {
      this.achievementArray.push({
        achievementId: null,
        achievementTypeId: null,
        achievementLevelTypeId: null,
        achievementRoleTypeId: null,
        story: null,
        place: null,
        startDate: null,
        endDate: null,
      });
    },
    deleteAchievement(achievement) {
      const { achievementArray, removeArrayElementById } = this;

      if (achievement.achievementId) {
        this.requestRemoveAchievement(achievement);
      }

      const achievementIndex = achievementArray.indexOf(achievement);
      this.achievementArray = removeArrayElementById(
        achievementArray,
        achievementIndex,
      );
    },
    validateAchievement(achievement, index) {
      const {
        achievementsData,
        isAchievementFormFilled,
        removeTimezoneOffset,
        isTwoObjectsEqual,
      } = this;
      this.$refs.achievement[index].validate();

      if (isAchievementFormFilled(achievement)) {
        const { achievementId } = achievement;

        if (achievementId) {
          const addedAchievement = achievementsData.find(
            (achievementItem) =>
              achievementItem.achievementId === achievementId,
          );

          const oldData = {
            achievementId: addedAchievement.achievementId,
            achievementTypeId:
              addedAchievement.achievementType.achievementTypeId,
            achievementLevelTypeId:
              addedAchievement.achievementLevelType.achievementLevelTypeId,
            achievementRoleTypeId:
              addedAchievement.achievementRoleType.achievementRoleTypeId,
            story: addedAchievement.story,
            place: addedAchievement.place,
            startDate: addedAchievement.startDate,
            endDate: addedAchievement.endDate,
          };

          const newData = {
            achievementId: achievement.achievementId,
            achievementTypeId: achievement.achievementTypeId,
            achievementLevelTypeId: achievement.achievementLevelTypeId,
            achievementRoleTypeId: achievement.achievementRoleTypeId,
            story: achievement.story,
            place: achievement.place,
            startDate: achievement.startDate,
            endDate: achievement.endDate,
          };

          if (isTwoObjectsEqual(newData, oldData)) {
            this.showMessage({
              type: 'error',
              message: 'There are no changes to be saved',
            });
          } else {
            this.requestUpdateAchievement(achievement);
          }
        } else {
          this.requestAddAchievement(achievement);
        }
      }
    },
    isAchievementFormFilled(achievement) {
      const requiredFields = {
        achievementTypeId: achievement.achievementTypeId,
        achievementLevelTypeId: achievement.achievementLevelTypeId,
        achievementRoleTypeId: achievement.achievementRoleTypeId,
        startDate: achievement.startDate,
      };

      return Object.values(requiredFields).every((value) => !!value);
    },
    toggleAchievementsVisibility() {
      this.isAchievementsVisible = !this.isAchievementsVisible;
    },
    onStartDayChanged(date, achievement) {
      const currentEndDate = achievement.endDate;

      if (!currentEndDate) {
        let newEndDate = new Date(date);
        newEndDate.setDate(newEndDate.getDate() + 7);

        achievement.endDate = newEndDate;
      }
    },
  },
  created() {
    const { achievementsData } = this;
    if (achievementsData) {
      this.setAchievementsData();
    }

    this.fetchAchievementTypeDescriptions();
    this.fetchAchievementLevelTypeDescriptions();
    this.fetchAchievementRoleTypeDescriptions();

    this.isAchievementsVisible = this.achievementArray.length === 0;
  },
  watch: {
    achievementsData(newVal) {
      if (newVal) {
        this.setAchievementsData();
      }
    },
  },
};
</script>

<style lang="scss">
.activity-cv-block {
  position: relative;
  margin-top: var(--global-dimension-size-300);

  &__visibility-toggler {
    position: absolute;
    top: var(--global-dimension-size-250);
    right: 0;
  }
  &__item-form {
    margin-bottom: var(--global-dimension-size-500);
  }
  &__buttons {
    display: flex;
    justify-content: space-between;
  }
  &__remove-button {
  }

  &--visible {
  }

  &--last {
    margin-bottom: var(--global-dimension-size-600);
  }
}
</style>
